import {useEffect, useState} from "react"
import {updateToken, useGetAuthToken} from "../../store/api/authSlice"
import {useAuth0} from "@auth0/auth0-react"
import {useDispatch} from "../../store/Store"

// These are the errors silent auth throws when it can't complete without
// showing UI. They happen the first time we request a token for the geocoder
// audience the user hasn't consented to yet, because the hidden iframe used by
// getAccessTokenSilently cannot render a consent screen.
const INTERACTION_REQUIRED = ["consent_required", "login_required", "interaction_required", "missing_refresh_token"]

export const useFetchToken = () => {
    const token = useGetAuthToken()
    const {getAccessTokenSilently, getAccessTokenWithPopup, user} = useAuth0()
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let cancelled = false

        const authorizationParams = {
            audience: process.env.REACT_APP_AUTH0_GEOCODER_AUDIENCE,
        }

        const fetchToken = async () => {
            try {
                const r = await getAccessTokenSilently({
                    detailedResponse: true,
                    authorizationParams,
                })
                if (!cancelled) dispatch(updateToken(r.access_token))
            } catch (error: any) {
                // Fall back to an interactive popup so the user can grant consent,
                // then retrieve the token. Popup (not redirect) keeps app state.
                if (INTERACTION_REQUIRED.includes(error?.error)) {
                    const accessToken = await getAccessTokenWithPopup({authorizationParams})
                    if (accessToken && !cancelled) dispatch(updateToken(accessToken))
                } else {
                    throw error
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        fetchToken().catch((error) => {
            // Never leave the loader spinning: log and let the app render so the
            // failure surfaces instead of hanging on <Loader/> forever.
            console.error("Failed to fetch access token", error)
            if (!cancelled) setLoading(false)
        })

        return () => {
            cancelled = true
        }
    }, [dispatch, getAccessTokenSilently, getAccessTokenWithPopup])

    return {isLoading: loading, token, user}
}
